/**
 * WAV（RIFF / 非圧縮 PCM）をデコードせずに直接扱うユーティリティ。
 *
 * `decodeAudioData` は AudioContext のサンプルレートへリサンプリングするため、
 * 44.1kHz / 96kHz / 24bit といった素材が再生環境のレートに引きずられて変質する。
 * WAV は「ヘッダ + 生 PCM の連続」なので、ヘッダを読んでバイト範囲をコピーすれば
 * 無変換のまま切り出せる。サウンドライブラリの主要素材である WAV はこの経路を通し、
 * 書き出しをバイト完全一致にする。
 *
 * 圧縮形式（mp3 / m4a / FLAC 等）はここでは扱わず、呼び出し側が decode に
 * フォールバックする。
 */

import { WaveformPeaks } from './peaks';

/** ヘッダ探索のために先読みするバイト数（bext / iXML 等の大きなチャンクを見込む） */
export const WAV_HEADER_PROBE_BYTES = 128 * 1024;

/** 1 ペアあたりに読むサンプル数の上限（長尺ファイルでメインスレッドを塞がないため） */
const MAX_SAMPLES_PER_PAIR = 4096;

/** 拡張チャンクを持たない標準的な WAV ヘッダ（RIFF + fmt + data）のバイト数 */
export const WAV_HEADER_BYTES = 44;

/** WAV を書き出すのに最低限必要なフォーマット情報 */
export interface WavFormat {
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
  /** 'int': 整数 PCM（format 1） / 'float': IEEE 浮動小数（format 3） */
  encoding: 'int' | 'float';
}

/** 既存ファイルから読み取った、data チャンクの位置まで含む完全なヘッダ情報 */
export interface WavHeader extends WavFormat {
  /** 1 フレーム（全チャンネル 1 サンプル分）のバイト数 */
  blockAlign: number;
  /** data チャンク本体の開始オフセット（ファイル先頭から） */
  dataOffset: number;
  /** data チャンク本体のバイト数 */
  dataLength: number;
  totalFrames: number;
  duration: number;
}

const RIFF = 0x52494646; // 'RIFF'
const WAVE = 0x57415645; // 'WAVE'
const FMT_ = 0x666d7420; // 'fmt '
const DATA = 0x64617461; // 'data'

/**
 * WAV ヘッダを解析する。WAV でない / 非対応の圧縮形式なら null を返す。
 *
 * `bytes` はファイル全体でも先頭数十 KB でもよい（Range 取得したヘッダ領域を
 * そのまま渡せる）。data チャンクが見つからなかった場合も null になるため、
 * 先読み量が足りないときは WAV_HEADER_PROBE_BYTES を増やして再試行する。
 */
export function parseWavHeader(bytes: ArrayBuffer): WavHeader | null {
  if (bytes.byteLength < WAV_HEADER_BYTES) return null;
  const view = new DataView(bytes);
  if (view.getUint32(0, false) !== RIFF || view.getUint32(8, false) !== WAVE) return null;

  let audioFormat = 0;
  let channels = 0;
  let sampleRate = 0;
  let bitsPerSample = 0;
  let blockAlign = 0;
  let dataOffset = -1;
  let dataLength = 0;

  // チャンクを順に辿る。各チャンクは id(4) + size(4) + body（偶数境界パディング）
  let offset = 12;
  while (offset + 8 <= bytes.byteLength) {
    const chunkId = view.getUint32(offset, false);
    const chunkSize = view.getUint32(offset + 4, true);
    const body = offset + 8;

    if (chunkId === FMT_ && body + 16 <= bytes.byteLength) {
      audioFormat = view.getUint16(body, true);
      channels = view.getUint16(body + 2, true);
      sampleRate = view.getUint32(body + 4, true);
      blockAlign = view.getUint16(body + 12, true);
      bitsPerSample = view.getUint16(body + 14, true);
      // WAVE_FORMAT_EXTENSIBLE は実フォーマットを SubFormat GUID の先頭 2 バイトが持つ
      if (audioFormat === 0xfffe && body + 26 <= bytes.byteLength) {
        audioFormat = view.getUint16(body + 24, true);
      }
    } else if (chunkId === DATA) {
      dataOffset = body;
      // size 0 / 0xFFFFFFFF のストリーミング WAV は残り全体を data とみなす
      dataLength =
        chunkSize > 0 && chunkSize !== 0xffffffff
          ? chunkSize
          : Math.max(0, bytes.byteLength - body);
      break;
    }

    offset = body + chunkSize + (chunkSize % 2);
  }

  if (dataOffset < 0 || channels <= 0 || sampleRate <= 0 || bitsPerSample <= 0) return null;
  if (audioFormat !== 1 && audioFormat !== 3) return null;

  const align = blockAlign > 0 ? blockAlign : (channels * bitsPerSample) / 8;
  if (align <= 0) return null;

  const totalFrames = Math.floor(dataLength / align);
  return {
    sampleRate,
    channels,
    bitsPerSample,
    encoding: audioFormat === 3 ? 'float' : 'int',
    blockAlign: align,
    dataOffset,
    dataLength,
    totalFrames,
    duration: totalFrames / sampleRate,
  };
}

/** サンプル 1 個を [-1, 1] の実数として読む関数を作る。非対応ビット深度なら null */
function makeSampleReader(
  view: DataView,
  header: WavHeader,
): ((_byteOffset: number) => number) | null {
  const { bitsPerSample, encoding } = header;
  if (encoding === 'float') {
    if (bitsPerSample === 32) return (o) => view.getFloat32(o, true);
    if (bitsPerSample === 64) return (o) => view.getFloat64(o, true);
    return null;
  }
  if (bitsPerSample === 16) return (o) => view.getInt16(o, true) / 32768;
  if (bitsPerSample === 24) {
    return (o) => {
      // 24bit LE は下位 2 バイトを符号なし、最上位バイトを符号付きで組む
      const lo = view.getUint8(o);
      const mid = view.getUint8(o + 1);
      const hi = view.getInt8(o + 2);
      return ((hi << 16) | (mid << 8) | lo) / 8388608;
    };
  }
  if (bitsPerSample === 32) return (o) => view.getInt32(o, true) / 2147483648;
  // 8bit WAV は符号なし（128 が無音）
  if (bitsPerSample === 8) return (o) => (view.getUint8(o) - 128) / 128;
  return null;
}

/**
 * 生 PCM から min/max ペアのピークを算出する（AudioContext を経由しない）。
 *
 * 戻り値は既存の波形描画（WaveformCanvas / waveformRender）が期待する
 * チャンネルごとの min/max 交互配列。
 */
export function peaksFromPcm(
  bytes: ArrayBuffer,
  header: WavHeader,
  pairsPerSecond: number,
): WaveformPeaks | null {
  const view = new DataView(bytes);
  const readSample = makeSampleReader(view, header);
  if (!readSample) return null;

  const bytesPerSample = header.bitsPerSample / 8;
  const availableFrames = Math.min(
    header.totalFrames,
    Math.floor(Math.max(0, bytes.byteLength - header.dataOffset) / header.blockAlign),
  );
  if (availableFrames <= 0) return null;

  const totalPairs = Math.max(1, Math.ceil((availableFrames / header.sampleRate) * pairsPerSecond));
  const framesPerPair = Math.max(1, Math.floor(availableFrames / totalPairs));
  // 長尺で 1 ペアあたりのサンプルが多すぎる場合は間引く（表示用途では十分）
  const stride = Math.max(1, Math.ceil(framesPerPair / MAX_SAMPLES_PER_PAIR));

  const out: WaveformPeaks = [];
  for (let ch = 0; ch < header.channels; ch++) {
    const chOut = new Array<number>(totalPairs * 2);
    const chByteOffset = header.dataOffset + ch * bytesPerSample;
    for (let p = 0; p < totalPairs; p++) {
      const from = p * framesPerPair;
      const to = Math.min(availableFrames, from + framesPerPair);
      let min = 0;
      let max = 0;
      for (let f = from; f < to; f += stride) {
        const v = readSample(chByteOffset + f * header.blockAlign);
        if (v < min) min = v;
        if (v > max) max = v;
      }
      chOut[p * 2] = Math.round(min * 100) / 100;
      chOut[p * 2 + 1] = Math.round(max * 100) / 100;
    }
    out.push(chOut);
  }
  return out;
}

/** 秒 → フレーム番号（data 範囲にクランプ） */
export function frameAt(header: WavHeader, seconds: number): number {
  const frame = Math.round(seconds * header.sampleRate);
  return Math.max(0, Math.min(header.totalFrames, frame));
}

/**
 * 標準 WAV ヘッダを組む。原本の切り出しと再エンコード書き出しの共通処理。
 *
 * 元が WAVE_FORMAT_EXTENSIBLE でも、PCM(1) / float(3) の素直な形で書き出す方が
 * DAW との互換性が高い。
 */
export function buildWavHeader(format: WavFormat, dataByteLength: number): ArrayBuffer {
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES);
  const view = new DataView(buffer);
  const writeAscii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };
  const blockAlign = (format.channels * format.bitsPerSample) / 8;

  writeAscii(0, 'RIFF');
  view.setUint32(4, WAV_HEADER_BYTES - 8 + dataByteLength, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format.encoding === 'float' ? 3 : 1, true);
  view.setUint16(22, format.channels, true);
  view.setUint32(24, format.sampleRate, true);
  view.setUint32(28, format.sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, format.bitsPerSample, true);
  writeAscii(36, 'data');
  view.setUint32(40, dataByteLength, true);
  return buffer;
}

/**
 * data チャンクのバイト列から範囲を切り出して WAV Blob を作る。
 *
 * `pcm` は data チャンク本体のみ（`dataOffset` 適用済み）を渡す。
 * サンプル値には一切触れないため、元ファイルとバイト単位で一致する。
 */
export function buildWavBlobFromPcm(
  pcm: ArrayBuffer | Uint8Array<ArrayBuffer>,
  header: WavHeader,
): Blob {
  const body = pcm instanceof Uint8Array ? pcm : new Uint8Array(pcm);
  return new Blob([buildWavHeader(header, body.byteLength), body], { type: 'audio/wav' });
}

/** ファイル全体を保持している場合の範囲切り出し（秒指定） */
export function sliceWavFromFile(
  fileBytes: ArrayBuffer,
  header: WavHeader,
  startSeconds: number,
  endSeconds: number,
): Blob {
  const startFrame = frameAt(header, startSeconds);
  const endFrame = Math.max(startFrame, frameAt(header, endSeconds));
  // 手元のバイト数を必ず上限にする（部分取得のバッファを渡されても例外にしない）
  const limit = Math.min(header.dataOffset + header.dataLength, fileBytes.byteLength);
  const from = Math.min(limit, header.dataOffset + startFrame * header.blockAlign);
  const to = Math.min(limit, header.dataOffset + endFrame * header.blockAlign);
  return buildWavBlobFromPcm(new Uint8Array(fileBytes, from, Math.max(0, to - from)), header);
}

/** 範囲切り出しに必要な HTTP Range ヘッダ値（inclusive）を求める */
export function rangeForSelection(
  header: WavHeader,
  startSeconds: number,
  endSeconds: number,
): { start: number; end: number; byteLength: number } {
  const startFrame = frameAt(header, startSeconds);
  const endFrame = Math.max(startFrame, frameAt(header, endSeconds));
  const start = header.dataOffset + startFrame * header.blockAlign;
  const end = Math.min(
    header.dataOffset + header.dataLength,
    header.dataOffset + endFrame * header.blockAlign,
  );
  return { start, end: Math.max(start, end - 1), byteLength: Math.max(0, end - start) };
}
